import { DefaultButton, PrimaryButton, Stack } from '@fluentui/react';
import React from 'react';
import { toast } from 'react-toastify';
import PanelWrapper from '../../Common/PanelWrapper';
import ToggleField from '../../Common/ToggleField';
import DataField from '../../Common/DataField';
import ConditionalSetting from '../../Common/ConditionalSetting';
import {
    useGetPaymentProcessorSetupQuery,
    useSaveStripeConfigMutation
} from '../../../store/endpoints/StorefrontSettingsEndpoints';

interface IStripeConfigPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
}

const StripeConfigPanel: React.FC<IStripeConfigPanelProps> = (props) => {
    const setup = useGetPaymentProcessorSetupQuery();
    const [saveStripeConfig] = useSaveStripeConfigMutation();

    const [createInvoice, setCreateInvoice] = React.useState<boolean>(false);
    const [invoiceDescription, setInvoiceDescription] = React.useState<string>('');
    const [invoiceFooter, setInvoiceFooter] = React.useState<string>('');
    const [collectPhoneNumber, setCollectPhoneNumber] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);

    // Seed field state from the saved config whenever the panel (re)opens.
    React.useEffect(() => {
        if (!props.isOpen) return;
        const data = setup.data;
        setCreateInvoice(data?.stripeCreateInvoice ?? false);
        setInvoiceDescription(data?.stripeInvoiceDescription ?? '');
        setInvoiceFooter(data?.stripeInvoiceFooter ?? '');
        setCollectPhoneNumber(data?.stripeCollectPhoneNumber ?? false);
    }, [props.isOpen, setup.data]);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        const toastId = toast.loading('Saving Stripe configuration…');
        try {
            const result = await saveStripeConfig({
                createInvoice,
                invoiceDescription: createInvoice ? invoiceDescription : undefined,
                invoiceFooter: createInvoice ? invoiceFooter : undefined,
                collectPhoneNumber
            }).unwrap();
            if (result.success) {
                toast.update(toastId, { type: 'success', render: 'Stripe configuration saved', isLoading: false, autoClose: 3000 });
                props.onDismiss();
            } else {
                toast.update(toastId, { type: 'error', render: result.message || 'Failed to save Stripe configuration', isLoading: false, autoClose: 4000 });
            }
        } catch {
            toast.update(toastId, { type: 'error', render: 'Failed to save Stripe configuration', isLoading: false, autoClose: 4000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title='Stripe Configuration'
            isFooterAtBottom
            footerButtons={[
                <PrimaryButton key='save' text='Save' iconProps={{ iconName: 'Save' }} disabled={saving} onClick={save} />,
                <DefaultButton key='cancel' text='Cancel' disabled={saving} onClick={props.onDismiss} />
            ]}
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                <ToggleField
                    settingId='storefront.global.ordering.stripeCreateInvoice'
                    label='Create Invoice'
                    checked={createInvoice}
                    onChange={(_e, checked) => setCreateInvoice(checked ?? false)}
                    toolTip='Generate a post-purchase Stripe invoice for each storefront order.'
                    inline
                />
                <ConditionalSetting
                    when={createInvoice}
                    ids={['storefront.global.ordering.stripeInvoiceDescription', 'storefront.global.ordering.stripeInvoiceFooter']}
                >
                    <DataField
                        settingId='storefront.global.ordering.stripeInvoiceDescription'
                        label='Invoice Description'
                        value={invoiceDescription}
                        multiline
                        rows={3}
                        toolTip={'Supports tags. Max length: 1000 characters'}
                        toolTipLocation='right'
                        onChange={(_e, value) => setInvoiceDescription(value ?? '')}
                        maxLength={1000}
                    />
                    <DataField
                        settingId='storefront.global.ordering.stripeInvoiceFooter'
                        label='Invoice Footer'
                        value={invoiceFooter}
                        multiline
                        rows={3}
                        toolTip={'Supports tags. Max length: 1000 characters'}
                        toolTipLocation='right'
                        onChange={(_e, value) => setInvoiceFooter(value ?? '')}
                        maxLength={1000}
                    />
                </ConditionalSetting>
                <ToggleField
                    settingId='storefront.global.ordering.stripeCollectPhoneNumber'
                    label='Collect Phone Number'
                    checked={collectPhoneNumber}
                    onChange={(_e, checked) => setCollectPhoneNumber(checked ?? false)}
                    toolTip="Collect the buyer's phone number on the Stripe checkout page."
                    inline
                />
            </Stack>
        </PanelWrapper>
    );
};

export default StripeConfigPanel;
